import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
} from '@/components/ui/table';
import { FileDown, Search, Receipt } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { TabsContent } from '@/components/ui/tabs';
import React, { useEffect, useState } from 'react';
import { ClinicTransaction, PaginationParams } from '@/lib/types';
import { clinicPaymentService } from '@/services/clinicPaymentService';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import SmartPagination from '@/components/ui/smart-pagination';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatToLocalDate } from '@/lib/utils';
import ConfirmDialog from '@/components/ui/confirm-dialog';
import TransactionDetailDrawer from '@/components/clinic/TransactionDetailDrawer';
import { useClinic } from '@/contexts/ClinicContext';

const Transactions = ({ dateRange }: { dateRange: { start_date: string; end_date: string } }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { clinic } = useClinic();
  const [searchTerm, setSearchTerm] = useState('');
  const [transactions, setTransactions] = useState<ClinicTransaction[]>([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    limit: 10,
    search: '',
    sortBy: 'created_at',
    sortOrder: 'DESC',
  });

  const fetchTransactions = async () => {
    if (!clinic?.id) return;
    setIsLoading(true);
    try {
      const clinicId = clinic.id;
      const response = await clinicPaymentService.getClinicTransactions(clinicId, {
        page: pagination.page,
        limit: pagination.limit,
        search: pagination.search,
        sortBy: 'created_at',
        sortOrder: 'DESC',
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
      });

      setTransactions(response.data.transactions);
      setPagination(prev => ({ ...prev, ...response.data.pagination }));
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load payment data',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportTransactions = async () => {
    setIsExporting(true);
    try {
      await clinicPaymentService.exportTransactions(clinic?.id, {
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
        format: 'csv',
      });
      toast({
        title: 'Export successful',
        description: 'The export has been sent to your email.',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to export transactions',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm !== pagination.search) {
        setPagination((prev) => ({
          ...prev,
          page: 1,
          search: searchTerm.trim(),
        }));
      }
    }, 800);

    return () => {
      clearTimeout(handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    fetchTransactions();
  }, [clinic?.id, pagination.page, pagination.limit, pagination.search, dateRange.start_date, dateRange.end_date]);

  const getStatusBadgeStyles = (status: string) => {
    switch (status.toLowerCase()) {
      case 'succeeded':
        return 'bg-brand-teal/10 text-brand-teal hover:bg-brand-teal/10';
      case 'pending':
        return 'bg-amber-100 text-amber-700 hover:bg-amber-100';
      case 'refunded':
        return 'bg-purple-100 text-purple-700 hover:bg-purple-100';
      case 'failed':
        return 'bg-red-100 text-red-700 hover:bg-red-100';
      default:
        return 'bg-slate-100 text-slate-600 hover:bg-slate-100';
    }
  };

  return (
    <TabsContent value="transactions" className="space-y-4">
      <Card>
        <CardHeader className="pb-2 px-3 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-4">
            <div className="flex items-start gap-2 sm:gap-3">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-brand-teal/10 flex items-center justify-center flex-shrink-0">
                <Receipt className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-brand-teal" strokeWidth={2} />
              </div>
              <div>
                <CardTitle className="text-slate-900 font-montserrat font-semibold text-sm sm:text-base">
                  Product Transaction History
                </CardTitle>
                <p className="text-slate-500 text-xs sm:text-sm mt-0.5">All your product sales and transactions</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="relative flex-1 sm:w-56">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search transactions..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-10 h-9 text-sm"
                />
              </div>
              {transactions.length > 0 && (
                <Button
                  size="sm"
                  onClick={() => setOpenConfirmDialog(true)}
                  disabled={isExporting}
                  className={cn(
                    'bg-brand-teal hover:bg-brand-teal/90 h-9',
                    isExporting && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <FileDown className={cn('mr-2 h-4 w-4', isExporting && 'animate-pulse')} />
                  {isExporting ? 'Exporting...' : 'Export'}
                </Button>
              )}
              <ConfirmDialog
                open={openConfirmDialog}
                onOpenChange={setOpenConfirmDialog}
                title="Export Transactions"
                description="Are you sure you want to export the transactions?"
                successMessage="Transactions exported successfully, an email will be sent to your clinic email"
                onConfirm={handleExportTransactions}
                isLoading={isExporting}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && (
            <div className="px-3 sm:px-6 py-4">
              <SkeletonCard />
            </div>
          )}
          
          {transactions.length === 0 && !isLoading && (
            <div className="text-center py-10 sm:py-14 px-4">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Receipt className="h-7 w-7 sm:h-8 sm:w-8 text-slate-300" strokeWidth={1.5} />
              </div>
              <p className="font-medium text-slate-900 text-sm sm:text-base">No transactions to display</p>
              <p className="text-slate-500 text-xs sm:text-sm mt-1">
                Transactions will appear here once you start receiving payments
              </p>
            </div>
          )}
          
          {transactions.length > 0 && !isLoading && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Transaction ID</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map(transaction => (
                    <TableRow 
                      key={transaction.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => {
                        setSelectedTransactionId(transaction.id);
                        setDetailDrawerOpen(true);
                      }}
                    >
                      <TableCell className="font-mono text-sm">
                        {transaction.id}
                      </TableCell>
                      <TableCell>{formatToLocalDate(transaction.processed_at, "MM/DD/YYYY hh:mm a")}</TableCell>
                      <TableCell>
                        {transaction.patient.full_name}
                      </TableCell>
                      <TableCell className="font-medium">${transaction.amount.toFixed(2)}</TableCell>
                      <TableCell className="text-center">
                        <Badge
                          className={cn(
                            'uppercase text-[10px] sm:text-xs px-2 py-0.5',
                            getStatusBadgeStyles(transaction.status)
                          )}
                        >
                          {transaction.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="border-t border-slate-200 px-2 sm:px-4 py-3 flex flex-col sm:flex-row items-center sm:justify-between gap-3">
              <SmartPagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={page => setPagination(prev => ({ ...prev, page }))}
                className="mx-0 justify-center sm:justify-start"
                maxVisiblePages={3}
              />
              <p className="text-xs text-slate-500 whitespace-nowrap">
                Total: <span className="font-medium text-slate-700">{pagination.totalItems || 0}</span> Transactions
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transaction Detail Drawer */}
      {selectedTransactionId && (
        <TransactionDetailDrawer
          open={detailDrawerOpen}
          onOpenChange={(open) => {
            setDetailDrawerOpen(open);
            if (!open) {
              setSelectedTransactionId(null);
            }
          }}
          transactionId={selectedTransactionId}
        />
      )}
    </TabsContent>
  );
};

const SkeletonCard = () => {
  return (
    <div className="space-y-3">
      {Array.from({ length: 8 }).map((_, index) => (
        <div key={index} className="flex items-center gap-4 p-2">
          <Skeleton className="h-4 w-32 rounded" />
          <Skeleton className="h-4 w-36 rounded" />
          <Skeleton className="h-4 w-28 rounded" />
          <Skeleton className="h-4 w-16 rounded ml-auto" />
          <Skeleton className="h-6 w-20 rounded-full" />
        </div>
      ))}
    </div>
  );
};

export default Transactions;
